import {
  ArrowRightIcon,
  Edit01Icon,
  XIcon,
} from '@langchain/untitled-ui-icons';
import { AutocompleteGetTagProps } from '@mui/base/useAutocomplete';
import { Autocomplete, FormLabel } from '@mui/joy';

import { useState } from 'react';

import { Badge, Tooltip } from '@/design-system';
import { cn } from '@/utils/tailwind';

import { useFileUploadTemplateStore } from '../hooks/useFileUploadTemplateStore';

export function FileDatasetKeysSelector() {
  const filePreview = useFileUploadTemplateStore((state) => state.filePreview);

  const inputKeys = useFileUploadTemplateStore((state) => state.inputKeys);
  const outputKeys = useFileUploadTemplateStore((state) => state.outputKeys);
  const metadataKeys = useFileUploadTemplateStore(
    (state) => state.metadataKeys
  );
  const setInputKeys = useFileUploadTemplateStore(
    (state) => state.setInputKeys
  );
  const setOutputKeys = useFileUploadTemplateStore(
    (state) => state.setOutputKeys
  );
  const setMetadataKeys = useFileUploadTemplateStore(
    (state) => state.setMetadataKeys
  );

  const inputKeyMappings = useFileUploadTemplateStore(
    (state) => state.inputKeyMappings
  );
  const outputKeyMappings = useFileUploadTemplateStore(
    (state) => state.outputKeyMappings
  );
  const metadataKeyMappings = useFileUploadTemplateStore(
    (state) => state.metadataKeyMappings
  );
  const setInputKeyMapping = useFileUploadTemplateStore(
    (state) => state.setInputKeyMapping
  );
  const setOutputKeyMapping = useFileUploadTemplateStore(
    (state) => state.setOutputKeyMapping
  );
  const setMetadataKeyMapping = useFileUploadTemplateStore(
    (state) => state.setMetadataKeyMapping
  );

  const csvError = useFileUploadTemplateStore((state) => state.error);

  return (
    filePreview &&
    inputKeys &&
    !csvError && (
      <div className="flex flex-col gap-4">
        <div className="relative flex flex-col gap-2">
          <FormLabel
            sx={{
              fontSize: 'small',
              fontWeight: 'medium',
            }}
          >
            Input fields
          </FormLabel>
          <Autocomplete
            data-testid="input-keys-autocomplete"
            size="sm"
            onChange={(_, value) => {
              setInputKeys(value);
            }}
            value={inputKeys}
            options={filePreview.columns}
            slotProps={{
              listbox: {
                disablePortal: true,
              },
            }}
            sx={{
              minHeight: '44px',
            }}
            freeSolo
            disableClearable
            multiple
            forcePopupIcon={false}
            renderTags={(tagValue, getTagProps) => {
              return tagValue.map((option, index) => (
                <EditableTag
                  {...getTagProps({ index })}
                  onDelete={() => {
                    setInputKeys(
                      inputKeys
                        ? inputKeys.filter((item) => item !== option)
                        : []
                    );
                    setInputKeyMapping(option, null);
                  }}
                  label={option}
                  isFirstIndex={index === 0}
                  key={index}
                  keyMappings={inputKeyMappings}
                  setKeyMapping={setInputKeyMapping}
                  allKeys={[
                    ...(inputKeys || []),
                    ...(outputKeys || []),
                    ...(metadataKeys || []),
                  ]}
                />
              ));
            }}
          />
        </div>
        <div className="relative flex flex-col gap-1">
          <FormLabel
            sx={{
              fontSize: 'small',
              fontWeight: 'medium',
            }}
          >
            Output fields
          </FormLabel>
          <Autocomplete
            data-testid="output-keys-autocomplete"
            size="sm"
            onChange={(_, value) => {
              setOutputKeys(value);
            }}
            value={outputKeys ?? []}
            options={filePreview.columns}
            slotProps={{
              listbox: {
                disablePortal: true,
              },
            }}
            sx={{
              minHeight: '44px',
            }}
            freeSolo
            multiple
            forcePopupIcon={false}
            renderTags={(tagValue, getTagProps) => {
              return tagValue.map((option, index) => (
                <EditableTag
                  {...getTagProps({ index })}
                  onDelete={() => {
                    setOutputKeys(
                      outputKeys
                        ? outputKeys.filter((item) => item !== option)
                        : []
                    );
                    setOutputKeyMapping(option, null);
                  }}
                  label={option}
                  isFirstIndex={index === 0}
                  key={index}
                  keyMappings={outputKeyMappings}
                  setKeyMapping={setOutputKeyMapping}
                  allKeys={[
                    ...(inputKeys || []),
                    ...(outputKeys || []),
                    ...(metadataKeys || []),
                  ]}
                />
              ));
            }}
          />
        </div>
        <div className="relative flex flex-col gap-1">
          <FormLabel
            sx={{
              fontSize: 'small',
              fontWeight: 'medium',
            }}
          >
            Metadata fields
          </FormLabel>
          <Autocomplete
            data-testid="metadata-keys-autocomplete"
            size="sm"
            onChange={(_, value) => {
              setMetadataKeys(value);
            }}
            value={metadataKeys ?? []}
            options={filePreview.columns}
            slotProps={{
              listbox: {
                disablePortal: true,
              },
            }}
            sx={{
              minHeight: '44px',
            }}
            freeSolo
            multiple
            forcePopupIcon={false}
            renderTags={(tagValue, getTagProps) => {
              return tagValue.map((option, index) => (
                <EditableTag
                  {...getTagProps({ index })}
                  onDelete={() => {
                    setMetadataKeys(
                      metadataKeys
                        ? metadataKeys.filter((item) => item !== option)
                        : []
                    );
                    setMetadataKeyMapping(option, null);
                  }}
                  label={option}
                  isFirstIndex={index === 0}
                  key={index}
                  keyMappings={metadataKeyMappings}
                  setKeyMapping={setMetadataKeyMapping}
                  allKeys={[
                    ...(inputKeys || []),
                    ...(outputKeys || []),
                    ...(metadataKeys || []),
                  ]}
                />
              ));
            }}
          />
        </div>
      </div>
    )
  );
}

interface EditableTagProps extends ReturnType<AutocompleteGetTagProps> {
  label: string;
  isFirstIndex: boolean;
  keyMappings: Record<string, string>;
  setKeyMapping: (originalKey: string, newKey: string | null) => void;
  allKeys: string[];
}

function EditableTag(props: EditableTagProps) {
  const {
    label,
    onDelete,
    isFirstIndex,
    keyMappings,
    setKeyMapping,
    allKeys,
    ...other
  } = props;
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  const displayName = keyMappings[label] || label;

  const handleStartEdit = () => {
    setIsEditing(true);
    setError(null);
  };

  // Pass new key name in directly to avoid race condition with the input value
  const handleSubmit = (newKey: string) => {
    if (newKey === displayName) {
      setIsEditing(false);
      return;
    }

    const trimmedNewKey = newKey.trim();

    // If blank, clear the mapping (revert to original)
    if (!trimmedNewKey) {
      setKeyMapping(label, null);
      setError(null);
      setIsEditing(false);
      return;
    }

    // Check if the new key already exists
    const allRenamedValues = Object.values(keyMappings);

    if (
      trimmedNewKey !== label &&
      (allKeys.includes(trimmedNewKey) ||
        allRenamedValues.includes(trimmedNewKey))
    ) {
      setError('Key name already exists');
      return;
    }

    setKeyMapping(label, trimmedNewKey);
    setError(null);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(displayName);
    setError(null);
    setIsEditing(false);
  };

  const isRenamed = displayName !== label;

  if (isEditing) {
    return (
      <Tooltip title={error ? error : undefined} side="bottom">
        <div
          className={cn(
            'group mx-1 mb-[4px] mt-2 box-content flex h-6 items-center overflow-hidden rounded-md border border-primary border-secondary bg-gray-50 bg-secondary pl-2 pr-1 leading-6 outline-none focus:border-brand',
            isFirstIndex && 'ml-2',
            error && 'border-error bg-error-primary'
          )}
          {...other}
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
          }}
          title={error ? error : undefined}
        >
          <input
            className="min-w-0 border-none bg-transparent text-xs outline-none"
            placeholder={'Rename column...'}
            value={editValue}
            onChange={(e) => {
              setEditValue(e.target.value);
              setError(null);
            }}
            onBlur={() => handleSubmit(editValue)}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === 'Enter') {
                handleSubmit(editValue);
              } else if (e.key === 'Escape') {
                handleCancel();
              }
            }}
            autoFocus
            onClick={(e) => e.stopPropagation()}
          />
          <XIcon
            className="ml-1 h-3 w-3 cursor-pointer hover:text-tertiary"
            onClick={(e) => {
              e.stopPropagation();
              setEditValue('');
              handleSubmit('');
            }}
            data-testid="CloseIcon"
          />
        </div>
      </Tooltip>
    );
  }

  return (
    <Tooltip title="Click to rename" side="bottom">
      <div>
        <Badge
          color={error ? 'error' : 'secondary'}
          size="sm"
          rounded="sm"
          className={cn(
            'group mx-1 mb-[4px] mt-2 cursor-pointer hover:border-brand',
            isFirstIndex && 'ml-2'
          )}
          {...other}
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            handleStartEdit();
          }}
          title={error ? error : undefined}
        >
          <div className="flex items-center">
            <span className="flex-1 overflow-hidden overflow-ellipsis whitespace-nowrap">
              {isRenamed ? (
                <>
                  <span className="text-muted-foreground">{label}</span>
                  <ArrowRightIcon className="text-muted-foreground mx-1 inline h-3 w-3" />
                  <span>{displayName}</span>
                </>
              ) : (
                displayName
              )}
            </span>
            <Edit01Icon className="ml-1 h-3 w-3" />
            <XIcon
              className="ml-1 h-3 w-3 cursor-pointer hover:text-tertiary"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                setEditValue('');
                onDelete(e);
              }}
              data-testid="CloseIcon"
            />
          </div>
        </Badge>
      </div>
    </Tooltip>
  );
}
